import React, { useState, useEffect } from 'react';
import { withdrawalsAPI, depositsAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { 
  ArrowUpFromLine,
  Clock,
  AlertCircle,
  Wallet,
  Bitcoin,
  DollarSign,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

export default function WithdrawalsPage() {
  const { user, refreshUser } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState(null);
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [withdrawalsRes, walletsRes] = await Promise.all([
        withdrawalsAPI.getAll(),
        depositsAPI.getWallets()
      ]);
      setWithdrawals(withdrawalsRes.data.withdrawals);
      setWallets(walletsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWithdrawal = async () => {
    const minWithdraw = wallets?.min_deposit || 10;
    if (!amount || parseFloat(amount) < minWithdraw) {
      toast.error(`Minimum withdrawal is $${minWithdraw}`);
      return;
    }

    if (!walletAddress) {
      toast.error('Please enter your wallet address');
      return;
    }

    if (parseFloat(amount) > (user?.balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    setCreating(true);
    try {
      await withdrawalsAPI.create({
        amount: parseFloat(amount),
        crypto_type: selectedCrypto,
        wallet_address: walletAddress
      });
      toast.success('Withdrawal request submitted!');
      setAmount('');
      setWalletAddress('');
      setDialogOpen(false);
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create withdrawal');
    } finally {
      setCreating(false);
    }
  };

  const cryptoOptions = [
    { id: 'BTC', name: 'Bitcoin', icon: Bitcoin, color: 'text-yellow-500' },
    { id: 'ETH', name: 'Ethereum', icon: () => <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-blue-500 flex items-center justify-center text-[10px] sm:text-xs font-bold text-white">E</div>, color: 'text-blue-400' },
    { id: 'USDT', name: 'Tether', icon: DollarSign, color: 'text-green-500' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      className="space-y-4 sm:space-y-6"
      data-testid="withdrawals-page"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-outfit font-bold text-white">Withdrawals</h1>
          <p className="text-white/60 mt-1 text-sm sm:text-base">Withdraw your funds to external wallets</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="new-withdrawal-btn">
              <ArrowUpFromLine className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Request Withdrawal
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0f1a] border-white/10 max-w-md mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
            <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-10">
              <X className="h-4 w-4 text-white/70 hover:text-white" />
              <span className="sr-only">Close</span>
            </DialogClose>
            <DialogHeader>
              <DialogTitle className="text-white text-lg">Request Withdrawal</DialogTitle>
              <DialogDescription className="text-white/60 text-sm">
                Enter your withdrawal details
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Balance Display */}
              <div className="p-3 sm:p-4 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-white/60 text-sm">Available Balance</span>
                  <span className="font-mono font-bold text-emerald-400">
                    ${user?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/80 text-sm">Amount (USD)</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="input-field"
                  data-testid="withdrawal-amount-input"
                />
                <p className="text-xs text-white/50">
                  Fee: $1.00 will be deducted from your balance
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-white/80 text-sm">Cryptocurrency</Label>
                <div className="grid grid-cols-3 gap-2">
                  {cryptoOptions.map((crypto) => (
                    <button
                      key={crypto.id}
                      type="button"
                      onClick={() => setSelectedCrypto(crypto.id)}
                      className={`p-3 rounded-lg border transition-all ${
                        selectedCrypto === crypto.id
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                      data-testid={`withdrawal-crypto-${crypto.id}`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        {typeof crypto.icon === 'function' ? <crypto.icon /> : <crypto.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${crypto.color}`} />}
                        <span className="text-xs text-white/70">{crypto.id}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/80 text-sm">Your {selectedCrypto} Wallet Address</Label>
                <div className="relative">
                  <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    type="text"
                    placeholder={`Enter your ${selectedCrypto} wallet address`}
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    className="input-field pl-10"
                    data-testid="withdrawal-wallet-input"
                  />
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-400/10 border border-yellow-400/30">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-yellow-400">
                  Double-check your wallet address. Withdrawals to incorrect addresses cannot be reversed.
                </p>
              </div>

              <Button
                type="button"
                onClick={handleCreateWithdrawal}
                className="btn-primary w-full"
                disabled={creating}
                data-testid="confirm-withdrawal-btn"
              >
                {creating ? 'Processing...' : 'Submit Withdrawal Request'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Info Cards */}
      <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
              <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <p className="text-white/60 text-xs sm:text-sm">Available</p>
              <p className="text-lg sm:text-2xl font-mono font-bold text-white truncate">
                ${user?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
              </p>
            </div>
          </div>
        </div>
        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0">
              <ArrowUpFromLine className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
            </div>
            <div className="min-w-0">
              <p className="text-white/60 text-xs sm:text-sm">Withdrawn</p>
              <p className="text-lg sm:text-2xl font-mono font-bold text-white truncate">
                ${withdrawals
                  .filter(w => w.status === 'completed')
                  .reduce((acc, w) => acc + w.amount, 0)
                  .toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Withdrawal History */}
      <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-outfit font-semibold text-white mb-4 sm:mb-6">Withdrawal History</h2>
        
        {/* Mobile Cards View */}
        <div className="sm:hidden space-y-3">
          {withdrawals.map((withdrawal) => (
            <div key={withdrawal.id} className="p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono font-bold text-hotpink-400">
                  -${withdrawal.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
                <span className={`status-badge ${
                  withdrawal.status === 'completed' ? 'status-badge-success' :
                  withdrawal.status === 'rejected' ? 'status-badge-danger' :
                  withdrawal.status === 'approved' ? 'status-badge-info' :
                  'status-badge-warning'
                }`}>
                  {withdrawal.status}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-white/50 mb-2">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {format(new Date(withdrawal.created_at), 'MMM dd, yyyy')}
                </span>
                <span>{withdrawal.crypto_type}</span>
              </div>
              <code className="text-[10px] text-white/40 block truncate">
                {withdrawal.wallet_address}
              </code>
            </div>
          ))}
          {withdrawals.length === 0 && (
            <p className="text-white/40 text-center py-8 text-sm">No withdrawals yet</p>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full" data-testid="withdrawals-table">
            <thead>
              <tr className="text-left text-white/50 text-sm border-b border-white/10">
                <th className="pb-3 font-medium">Date</th>
                <th className="pb-3 font-medium">Amount</th>
                <th className="pb-3 font-medium">Crypto</th>
                <th className="pb-3 font-medium hidden md:table-cell">Wallet</th>
                <th className="pb-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.map((withdrawal) => (
                <tr key={withdrawal.id} className="table-row">
                  <td className="py-4">
                    <div className="flex items-center gap-2 text-white/70 text-sm">
                      <Clock className="w-4 h-4" />
                      {format(new Date(withdrawal.created_at), 'MMM dd, yyyy HH:mm')}
                    </div>
                  </td>
                  <td className="py-4">
                    <span className="font-mono font-bold text-hotpink-400">
                      -${withdrawal.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                    <span className="text-xs text-white/40 block">
                      Fee: ${withdrawal.fee || '1.00'}
                    </span>
                  </td>
                  <td className="py-4">
                    <span className="text-white/70">{withdrawal.crypto_type}</span>
                  </td>
                  <td className="py-4 hidden md:table-cell">
                    <code className="text-xs text-white/50">
                      {withdrawal.wallet_address?.slice(0, 10)}...{withdrawal.wallet_address?.slice(-6)}
                    </code>
                  </td>
                  <td className="py-4">
                    <span className={`status-badge ${
                      withdrawal.status === 'completed' ? 'status-badge-success' :
                      withdrawal.status === 'rejected' ? 'status-badge-danger' :
                      withdrawal.status === 'approved' ? 'status-badge-info' :
                      'status-badge-warning'
                    }`}>
                      {withdrawal.status}
                    </span>
                  </td>
                </tr>
              ))}
              {withdrawals.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-white/40">
                    No withdrawals yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );
}
